import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Missing reset token. Please request a new password reset link.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-ewss-navy">
      <div className="max-w-md w-full px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-black/40 to-ewss-navy/40 backdrop-blur-sm rounded-2xl p-8 border border-ewss-cyan/30"
          data-testid="reset-password-card"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-ewss-cyan/10 rounded-xl flex items-center justify-center text-ewss-cyan mx-auto mb-4">
              <Lock className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Set a New Password</h2>
            <p className="text-gray-400">Enter and confirm your new password below.</p>
          </div>

          {done ? (
            <div
              className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-start gap-3"
              data-testid="reset-password-success"
            >
              <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-green-300 text-sm">
                Password reset successful! Redirecting you to the sign-in page...
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div
                  className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3"
                  data-testid="reset-password-error"
                >
                  <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="text-gray-500" size={20} />
                    </div>
                    <input
                      id="new-password"
                      type="password"
                      required
                      minLength={6}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                      placeholder="At least 6 characters"
                      data-testid="reset-password-new-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="text-gray-500" size={20} />
                    </div>
                    <input
                      id="confirm-password"
                      type="password"
                      required
                      minLength={6}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full pl-10 pr-3 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ewss-cyan focus:border-transparent"
                      placeholder="Re-enter new password"
                      data-testid="reset-password-confirm-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:shadow-[0_0_30px_rgba(23,145,255,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-ewss-cyan hover:underline font-semibold text-sm"
              data-testid="reset-password-back-to-login"
            >
              ← Back to sign in
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
